import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { LightTheme, Spacings, Text } from '@styles'

const Wrapper = styled.div`
	width: 100%;
`

const Track = styled.div`
	position: relative;
	width: 100%;
	height: ${(p) => p.$height || Text.EXTRA_SMALL};
	background: ${(p) => p.$trackColor || '#f3f3f3'};
	border-radius: ${(p) => p.$radius || Spacings.BASE};
	overflow: visible; /* allow tooltip to show */
	box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${LightTheme.palette.background.greenDark};
		outline-offset: 2px;
	}
`

const Fill = styled.div`
	height: 100%;
	width: ${(p) => `${p.$value}%`};
	background: ${(p) => p.$fillColor || LightTheme.palette.background.greenDark};
	border-radius: inherit;
	transition: width 600ms ease;
`

const Tooltip = styled.div`
	position: absolute;
	bottom: calc(100% + 8px);
	left: ${(p) => `${p.$pos}%`};
	transform: translateX(-50%);
	padding: 4px 6px;
	font-weight: 800;
	font-size: ${(p) => p.$fontSize || Text.EXTRA_SMALL};
	background: ${(p) => p.$tipBg || LightTheme.palette.background.light};
	color: ${(p) => p.$tipColor || LightTheme.palette.text.primary};
	border-radius: ${Spacings.SPACING_2B};
	white-space: nowrap;
	pointer-events: none;
	z-index: 1;

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 7px solid transparent;
		border-top-color: ${(p) => p.$tipBg || LightTheme.palette.background.light};
	}

	${(p) =>
		p.$showOnHover &&
		css`
			opacity: 0;
			transform: translateX(-50%) translateY(4px);
			transition: opacity 120ms ease, transform 120ms ease;
			${Track}:hover &,
      ${Track}:focus-within & {
				opacity: 1;
				transform: translateX(-50%) translateY(0);
			}
		`}
`

function clamp(n, min, max) {
	return Math.max(min, Math.min(max, n))
}

function ProgressBar({
	value = 0,
	height = Spacings.SPACING_2B,
	radius = Spacings.SPACING_1B,
	trackColor = '#f3f3f3',
	fillColor = null,
	tipBg = LightTheme.palette.text.greyFive,
	tipColor = LightTheme.palette.text.primary,
	label = null,
	fontSize = Text.EXTRA_SMALL,
	showOnHover = true,
}) {
	const clamped = useMemo(() => clamp(value, 0, 100), [value])
	const pos = useMemo(() => clamp(clamped, 6, 94), [clamped])
	const _fillColor = fillColor > 80 ? LightTheme.palette.text.green : fillColor > 50 ? LightTheme.palette.text.blue : LightTheme.palette.text.red
	return (
		<Wrapper>
			<Track
				$height={height}
				$radius={radius}
				$trackColor={trackColor}
				role='progressbar'
				aria-valuenow={Math.round(clamped)}
				aria-valuemin={0}
				aria-valuemax={100}
				tabIndex={0}
			>
				<Fill $value={clamped} $fillColor={_fillColor} />
				{clamped > 0 && (
					<Tooltip $pos={pos} $tipBg={tipBg} $tipColor={tipColor} $fontSize={fontSize} $showOnHover={showOnHover}>
						{Math.round(clamped)}%{label && <div>{label}</div>}
					</Tooltip>
				)}
			</Track>
		</Wrapper>
	)
}

export default ProgressBar
